import { useState } from 'react';

import { Account, AccountGroup, LifecycleStatus } from '@ndvr/portfolio-api';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useMoveAccount } from 'views/portfolio/account-queries';

interface MoveAccountModalProps {
    householdGUID: string;
    account: Account;
    accountGroups: AccountGroup[];
    onClose: () => void;
}

export const canMoveAccount = (account: Account) =>
    !account.originAccountGUID && !(Number(account.initialSecuredness) > 0);

export const MoveAccountModal = ({ householdGUID, account, accountGroups, onClose }: MoveAccountModalProps) => {
    const [destinationGUID, setDestinationGUID] = useState('');
    const { mutate: moveAccount, isPending } = useMoveAccount(householdGUID);
    const destinations = accountGroups.filter(
        (group) =>
            group.householdGUID === householdGUID &&
            group.accountGroupGUID !== account.accountGroupGUID &&
            group.lifecycleStatus !== LifecycleStatus.CLOSED &&
            !group.originAccountGroupGUID,
    );
    const destination = destinations.find((group) => group.accountGroupGUID === destinationGUID);
    const canMove = canMoveAccount(account);

    return (
        <Dialog open onOpenChange={(open) => !open && !isPending && onClose()}>
            <DialogContent>
                <form
                    onSubmit={(event) => {
                        event.preventDefault();
                        if (!destination || !canMove || isPending) return;
                        moveAccount(
                            { accountGUID: account.accountGUID, targetAccountGroupGUID: destination.accountGroupGUID },
                            { onSuccess: onClose },
                        );
                    }}
                >
                    <DialogHeader>
                        <DialogTitle>Move to portfolio</DialogTitle>
                        <DialogDescription>
                            Move {account.accountName.plainText} to another portfolio in this household. This may change
                            allocations, restrictions, and cashflow settings. Trading will be suspended for all live
                            accounts in the source and destination portfolios.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-2 py-6">
                        <Label htmlFor="move-account-destination">Destination portfolio</Label>
                        <Select
                            value={destinationGUID}
                            onValueChange={setDestinationGUID}
                            disabled={isPending || !canMove}
                        >
                            <SelectTrigger id="move-account-destination">
                                <SelectValue placeholder="Select portfolio" />
                            </SelectTrigger>
                            <SelectContent>
                                {destinations.map((group) => (
                                    <SelectItem key={group.accountGroupGUID} value={group.accountGroupGUID}>
                                        {group.accountGroupName.plainText || 'Unnamed portfolio'}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {!destinations.length && (
                            <p className="text-muted-foreground text-sm">No other portfolios available.</p>
                        )}
                        {!canMove && (
                            <p className="text-muted-foreground text-sm">
                                Secured accounts and edit clones cannot be moved.
                            </p>
                        )}
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" disabled={isPending} onClick={onClose}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={!destination || !canMove || isPending}>
                            {isPending ? 'Moving…' : 'Move account'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
